// ============================================================================
// Sesión compartida de la suite Dimo.
//
// El problema que resuelve: hasta agosto de 2026 había CINCO pantallas de login
// distintas. Entrabas en el Centro de Aplicaciones, pulsabas CRM y te volvía a
// pedir la contraseña; entrabas en CRM Admin y otra vez. Cada app guardaba su
// propio token en su propio `localStorage`, y `localStorage` no se comparte
// entre subdominios: app.dimoauto.com y crmadmin.dimoauto.com son dos cajones
// separados.
//
// La solución es una cookie en el dominio padre `.dimoauto.com`, que SÍ viaja a
// todos los subdominios. La escribe quien hace el login (normalmente el Centro
// de Aplicaciones) y la leen todas las demás al arrancar.
//
// Sobre la seguridad: la cookie NO es HttpOnly, porque el JavaScript de cada
// app tiene que poder leerla. Eso es exactamente la misma exposición que ya
// había con `localStorage`, ni más ni menos — no es un paso atrás. Lo que sí se
// gana es que desaparecen las contraseñas guardadas en claro que tenían algunas
// apps para el "recuérdame".
//
// Ojo: solo funciona dentro de `*.dimoauto.com`. La app de costes vive en
// `costes-app.dimoauto.es`, que es otro dominio registrable, así que esa se
// queda con su login propio hasta que se mueva de dominio.
// ============================================================================

const NOMBRE = "dimo_sesion";
const DOMINIO = ".dimoauto.com";
const DIAS = 7;

// Codifica en base64 aguantando acentos (btoa solo entiende latin-1).
function aBase64(texto) {
  return btoa(unescape(encodeURIComponent(texto)));
}
function deBase64(b64) {
  return decodeURIComponent(escape(atob(b64)));
}

// En local (localhost, 127.0.0.1, IP de Coolify) no se puede poner el dominio
// padre ni Secure: se escribe una cookie normal para poder probar.
function esProduccion() {
  return typeof window !== "undefined" && window.location.hostname.endsWith("dimoauto.com");
}

/**
 * Guarda la sesión para todo `*.dimoauto.com`.
 * @param {{access_token:string, refresh_token?:string, email:string,
 *          nombre?:string, rol?:string, expires_at?:number}} sesion
 */
export function guardarSesion(sesion) {
  if (!sesion || !sesion.access_token || !sesion.email) return false;
  try {
    const valor = aBase64(JSON.stringify({
      access_token: sesion.access_token,
      refresh_token: sesion.refresh_token || null,
      email: sesion.email,
      nombre: sesion.nombre || null,
      rol: sesion.rol || null,
      // Si el token no dice cuándo caduca, se le da la vida de la cookie.
      expira: sesion.expires_at ? sesion.expires_at * 1000 : Date.now() + DIAS * 864e5,
    }));
    const caduca = new Date(Date.now() + DIAS * 864e5).toUTCString();
    const partes = [`${NOMBRE}=${valor}`, "path=/", `expires=${caduca}`, "SameSite=Lax"];
    if (esProduccion()) partes.push(`domain=${DOMINIO}`, "Secure");
    document.cookie = partes.join("; ");
    return true;
  } catch (e) {
    console.warn("[sesión] no se ha podido guardar:", e?.message || e);
    return false;
  }
}

/**
 * Devuelve la sesión compartida, o null si no hay o ya caducó.
 * @returns {{access_token:string, refresh_token:string|null, email:string,
 *            nombre:string|null, rol:string|null, expira:number} | null}
 */
export function leerSesion() {
  try {
    const cruda = document.cookie.split("; ").find((c) => c.startsWith(`${NOMBRE}=`));
    if (!cruda) return null;
    const sesion = JSON.parse(deBase64(cruda.slice(NOMBRE.length + 1)));
    if (!sesion?.access_token || !sesion?.email) return null;
    // Un minuto de margen: no merece la pena entrar con un token que muere ya.
    if (sesion.expira && sesion.expira < Date.now() + 60000) { borrarSesion(); return null; }
    return sesion;
  } catch (e) {
    return null;
  }
}

/** Cierra la sesión en todas las apps del dominio. */
export function borrarSesion() {
  try {
    const partes = [`${NOMBRE}=`, "path=/", "expires=Thu, 01 Jan 1970 00:00:00 GMT", "SameSite=Lax"];
    if (esProduccion()) partes.push(`domain=${DOMINIO}`, "Secure");
    document.cookie = partes.join("; ");
  } catch (e) { /* nada que hacer */ }
}

/** ¿Estamos dentro de un iframe del Centro de Aplicaciones? */
export function esEmbebido() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("embed") === "1";
}

/**
 * Arranque con la sesión compartida.
 *
 * Lo llama cada app del CRM al montarse: si hay cookie de sesión, deja el token
 * donde la app ya lo busca (`sb-access-token`) y devuelve la fila de
 * `administradores` de ese correo. Si no hay cookie, o el correo no está dado
 * de alta como administrador activo, devuelve null y la app enseña su propio
 * login como toda la vida. Nunca lanza: si algo falla, se cae con elegancia al
 * comportamiento de antes.
 *
 * @param {string} supabaseUrl  URL del proyecto CRM
 * @param {string} anonKey      clave anon del proyecto CRM
 * @returns {Promise<object|null>} fila de `administradores` o null
 */
export async function entrarConSesionCompartida(supabaseUrl, anonKey) {
  const s = leerSesion();
  if (!s) return null;
  try {
    window.localStorage.setItem("sb-access-token", s.access_token);
    if (s.refresh_token) window.localStorage.setItem("sb-refresh-token", s.refresh_token);
  } catch (e) { /* modo privado */ }
  try {
    const url = `${supabaseUrl}/rest/v1/administradores?email=eq.${encodeURIComponent(s.email)}&activo=eq.true&select=*`;
    const r = await fetch(url, { headers: { apikey: anonKey, Authorization: `Bearer ${s.access_token}` } });
    if (!r.ok) return null;
    const filas = await r.json();
    return Array.isArray(filas) && filas.length > 0 ? filas[0] : null;
  } catch (e) {
    console.warn("[sesión] no se ha podido entrar con la sesión compartida:", e?.message || e);
    return null;
  }
}
